/**
 * Tap to Pay on iPhone via Stripe Terminal (Capacitor plugin).
 * Only use when Capacitor.getPlatform() === 'ios' and plugin is available.
 *
 * Flip to false (and drop com.apple.developer.proximity-reader.payment.acceptance
 * from App.entitlements) to show the tile as "Coming soon" instead.
 */
const TAP_TO_PAY_ENABLED = false;

import { supabase } from '../lib/supabaseClient';

export type TapToPayParams = {
  businessId: string;
  amount: number;
  paymentType: 'deposit' | 'final' | 'partial';
  bookingId?: string | null;
  proposalId?: string | null;
  description?: string;
};

export type TapToPayResult = { paymentIntentId: string };

/**
 * Run the full Tap to Pay flow: init/token → discover → connect → create PI → collect → confirm.
 * Caller must record the payment in DB and close the modal on success.
 *
 * Token provider + SDK init are DELEGATED to stripeReaderService: the native
 * SDK allows exactly one token provider per app session, and each request must
 * be answered exactly once — a second listener (as this file once had) makes
 * the extra answer fail with "Stripe Terminal do not pending
 * fetchConnectionToken" and can desync the token queue.
 */
export async function runTapToPayFlow(params: TapToPayParams): Promise<TapToPayResult> {
  const { StripeTerminal, TerminalConnectTypes } = await import(
    '@capacitor-community/stripe-terminal'
  );
  const { ensureInitialized, fetchConnectionToken } = await import('./stripeReaderService');
  const { businessId, amount, paymentType, bookingId, proposalId, description } = params;

  // 1) Shared SDK init + the app's single connection-token provider.
  await ensureInitialized(businessId);

  // 2) Terminal location (required to discover the Tap to Pay reader).
  const { locationId } = await fetchConnectionToken(businessId);
  if (!locationId) {
    throw new Error('Tap to Pay requires a Terminal location. Please try again.');
  }
  // 3) Discover the Tap to Pay reader (this phone)
  const { readers } = await StripeTerminal.discoverReaders({
    type: TerminalConnectTypes.TapToPay,
    locationId,
  });
  if (!readers?.length) {
    throw new Error(
      'Tap to Pay is not available on this device. Use an iPhone XS or later with a supported region.'
    );
  }

  // 4) Connect to the reader (this device)
  await StripeTerminal.connectReader({ reader: readers[0] });

  try {
    // 5) Create Terminal PaymentIntent (card_present) on backend
    const { data: piData, error: piError } = await supabase.functions.invoke(
      'create-terminal-payment-intent',
      {
        body: {
          businessId,
          amount,
          payment_type: paymentType,
          bookingId: bookingId || undefined,
          proposalId: proposalId || undefined,
          description: description || 'Tap to Pay payment',
        },
      }
    );
    if (piError || !piData?.paymentIntentId) {
      throw new Error(piData?.error || piError?.message || 'Failed to create payment');
    }
    const paymentIntentId = piData.paymentIntentId as string;

    // 6) Collect payment method (customer taps phone/card)
    await StripeTerminal.collectPaymentMethod({ paymentIntent: paymentIntentId });

    // 7) Confirm and capture
    await StripeTerminal.confirmPaymentIntent();

    // A successful charge means this device finished Apple's setup too.
    markTapToPaySetUp(businessId);
    return { paymentIntentId };
  } finally {
    await StripeTerminal.disconnectReader();
  }
}

/**
 * Check if Tap to Pay can be offered: native iOS, plugin available, and feature enabled (Apple entitlement approved).
 */
export async function isTapToPayAvailable(): Promise<boolean> {
  if (!TAP_TO_PAY_ENABLED) return false;
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.getPlatform() !== 'ios' || !Capacitor.isNativePlatform()) return false;
    const { StripeTerminal } = await import('@capacitor-community/stripe-terminal');
    return typeof StripeTerminal?.initialize === 'function';
  } catch {
    return false;
  }
}

/** True when this app build could offer Tap to Pay but the feature flag is off ("Coming soon"). */
export async function isTapToPayComingSoon(): Promise<boolean> {
  if (TAP_TO_PAY_ENABLED) return false;
  try {
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// ── One-time device setup (settings) ─────────────────────────────────────────
// Connecting this iPhone as a Tap to Pay reader outside of a sale triggers
// Apple's native Terms of Service sheet and configuration download the first
// time, so merchants finish the whole Apple handshake before their first
// customer is standing in front of them.

const setupKey = (businessId: string) => `nexus:tapToPaySetup:${businessId}`;

/** Whether Tap to Pay setup has been completed on this device for this business. */
export function isTapToPaySetUp(businessId: string): boolean {
  try {
    return localStorage.getItem(setupKey(businessId)) === '1';
  } catch {
    return false;
  }
}

export function markTapToPaySetUp(businessId: string): void {
  try {
    localStorage.setItem(setupKey(businessId), '1');
  } catch {
    /* storage unavailable — setup will simply be offered again */
  }
}

export function clearTapToPaySetUp(businessId: string): void {
  try {
    localStorage.removeItem(setupKey(businessId));
  } catch {
    /* ignore */
  }
}

/**
 * Run Tap to Pay setup without charging anything: init → discover this iPhone
 * as the reader → connect (iOS shows Apple's Tap to Pay terms + configuration
 * on first connect) → disconnect. Resolves once the device is ready.
 */
export async function setupTapToPay(businessId: string): Promise<void> {
  const { StripeTerminal, TerminalConnectTypes } = await import(
    '@capacitor-community/stripe-terminal'
  );
  const { ensureInitialized, fetchConnectionToken } = await import('./stripeReaderService');

  await ensureInitialized(businessId);
  const { locationId } = await fetchConnectionToken(businessId);
  if (!locationId) {
    throw new Error('Tap to Pay requires a Terminal location. Please try again.');
  }
  const { readers } = await StripeTerminal.discoverReaders({
    type: TerminalConnectTypes.TapToPay,
    locationId,
  });
  if (!readers?.length) {
    throw new Error(
      'Tap to Pay is not available on this device. Use an iPhone XS or later with a supported region.'
    );
  }
  await StripeTerminal.connectReader({ reader: readers[0] });
  await StripeTerminal.disconnectReader();
  markTapToPaySetUp(businessId);
}
